import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindCertificatesByOrganizationIdQueryResponse,
  FindCertificatesByOrganizationIdQueryParams,
} from "../types/FindCertificatesByOrganizationId"

type FindCertificatesByOrganizationIdClient = typeof client<
  FindCertificatesByOrganizationIdQueryResponse,
  Error,
  never
>
type FindCertificatesByOrganizationId = {
  data: FindCertificatesByOrganizationIdQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindCertificatesByOrganizationIdQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindCertificatesByOrganizationIdClient>>
  client: {
    parameters: Partial<Parameters<FindCertificatesByOrganizationIdClient>[0]>
    return: Awaited<ReturnType<FindCertificatesByOrganizationIdClient>>
  }
}
export const findCertificatesByOrganizationIdQueryKey = (
  params?: FindCertificatesByOrganizationId["queryParams"]
) =>
  [
    { url: "/v1/certificate/find-certificates-by-organization-id" },
    ...(params ? [params] : []),
  ] as const
export type FindCertificatesByOrganizationIdQueryKey = ReturnType<
  typeof findCertificatesByOrganizationIdQueryKey
>
export function findCertificatesByOrganizationIdQueryOptions<
  TData = FindCertificatesByOrganizationId["response"],
  TQueryData = FindCertificatesByOrganizationId["response"],
>(
  params?: FindCertificatesByOrganizationId["queryParams"],
  options: FindCertificatesByOrganizationId["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindCertificatesByOrganizationId["response"],
    FindCertificatesByOrganizationId["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findCertificatesByOrganizationIdQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindCertificatesByOrganizationId["data"],
        FindCertificatesByOrganizationId["error"]
      >({
        method: "get",
        url: `/v1/certificate/find-certificates-by-organization-id`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find certificates by organization id
 * @link /v1/certificate/find-certificates-by-organization-id
 */
export function useFindCertificatesByOrganizationId<
  TData = FindCertificatesByOrganizationId["response"],
  TQueryData = FindCertificatesByOrganizationId["response"],
  TQueryKey extends QueryKey = FindCertificatesByOrganizationIdQueryKey,
>(
  params?: FindCertificatesByOrganizationId["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindCertificatesByOrganizationId["response"],
        FindCertificatesByOrganizationId["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindCertificatesByOrganizationId["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindCertificatesByOrganizationId["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey =
    queryOptions?.queryKey ?? findCertificatesByOrganizationIdQueryKey(params)
  const query = useQuery<
    FindCertificatesByOrganizationId["data"],
    FindCertificatesByOrganizationId["error"],
    TData,
    any
  >({
    ...findCertificatesByOrganizationIdQueryOptions<TData, TQueryData>(
      params,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindCertificatesByOrganizationId["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
